import { DynamicSettings } from "Components/admin/DynamicSettings";
import { useMobileSearchboxModal } from "../mobile-searchbox-modal/useMobileSearchboxModal";
import { html } from "lit-html";
import classNames from "classnames";
import i18next from "i18next";
import { useState, useMemo } from "Shared/haunted/CustomHooks";
import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { mobileDateSelectorInputsTemplate } from "ComponentHelpers/MobileDateSelectorHelper";
import dayjs from "dayjs";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import { capitalizeAndRemoveDot } from "Services/common";
import getShowFarePriceForCulture from "Shared/helpers/farePriceHelper";
import { Timetable } from "./useTimetable";
import { calendarUtils } from "./calendarUtils";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { useNewFlightDateCalendar } from "./useNewFlightDateCalendar";
dayjs.extend(CustomParseFormat);

const DATE_SELECTOR_SCROLL_CONTAINER_CLASS = "js-date-selector-scroll-container";
const MONTH_INDEX_CLASS = "js-month-index";

export interface Props {
	culture: string;
	currentSearchManager: CurrentSearchManager;
	dynamicSettings: DynamicSettings;
	timetable: Timetable;
	onDepartureChange: (date: dayjs.Dayjs) => void;
	onReturnChange: (date: dayjs.Dayjs) => void;
}

export const useMobileSearchboxDateSelectorModal = (props: Props) => {
	const [openMonthIndexes, setOpenMonthIndexes] = useState<Set<number>>(new Set([0]));
	const [isSelectingReturn, setIsSelectingReturn] = useState<boolean>(false);

	const monthList = useMemo(() => {
		const months: { date: dayjs.Dayjs; index: number }[] = [];
		const start = props.timetable.minDepartureDate || dayjs();
		const end = props.timetable.maxDepartureDate || dayjs().add(12, "month");

		let current = start.startOf("month");
		let index = 0;

		while (current.isSameOrBefore(end, "month") && index < 12) {
			months.push({ date: current, index });
			current = current.add(1, "month");
			index++;
		}

		return months;
	}, [props.timetable.minDepartureDate, props.timetable.maxDepartureDate]);

	const currentAvailableSchedules = isSelectingReturn
		? props.timetable.inboundAvailableDates
		: props.timetable.outboundAvailableDates;
	const currentDisabledDates = isSelectingReturn
		? props.timetable.disabledReturnDates
		: props.timetable.disabledDepartureDates;
	const currentMin = isSelectingReturn ? props.timetable.minReturnDate : props.timetable.minDepartureDate;
	const currentMax = isSelectingReturn ? props.timetable.maxReturnDate : props.timetable.maxDepartureDate;
	const currentSelectedDate = isSelectingReturn
		? props.currentSearchManager.currentSearch.returnDate
		: props.currentSearchManager.currentSearch.departureDate;

	const rangeStart = isSelectingReturn ? props.currentSearchManager.currentSearch.departureDate : undefined;

	const usePrices =
		getShowFarePriceForCulture(props.dynamicSettings.ShowFarePricesCultures, props.culture) &&
		!props.currentSearchManager.currentSearch.useAmericanMiles;

	const { getMonthCalendar } = calendarUtils();

	const findMonthIndex = (date: dayjs.Dayjs | undefined): number => {
		if (!date) return -1;
		return monthList.findIndex((m) => m.date.isSame(date, "month"));
	};

	const scrollToMonthIndex = (index: number) => {
		window.setTimeout(() => {
			const monthElement = document.querySelector(`.${MONTH_INDEX_CLASS}-${index}`) as HTMLElement;
			const scrollContainer = document.querySelector(`.${DATE_SELECTOR_SCROLL_CONTAINER_CLASS}`) as HTMLElement;

			if (!monthElement || !scrollContainer) return;

			const containerTop = scrollContainer.getBoundingClientRect().top;
			const elementTop = monthElement.getBoundingClientRect().top;
			const scrollOffset = elementTop - containerTop;

			scrollContainer.scrollTo({
				top: scrollContainer.scrollTop + scrollOffset,
				behavior: "smooth",
			});
		}, 500);
	};

	const getMonthsToOpen = (): Set<number> => {
		const monthsToOpen = new Set<number>();

		monthList.forEach((month) => {
			const hasSelectableDates = currentAvailableSchedules.some((schedule) => {
				const isInMonth = schedule.departureDate.isSame(month.date, "month");
				if (!isInMonth) return false;

				const effectiveMin = isSelectingReturn
					? props.currentSearchManager.currentSearch.departureDate || currentMin
					: currentMin;

				const isTooEarly = schedule.departureDate.isBefore(effectiveMin, "date");

				const isDisabled =
					isTooEarly ||
					schedule.departureDate.isAfter(currentMax, "date") ||
					currentDisabledDates.some((date) => date.isSame(schedule.departureDate, "date"));

				return !isDisabled;
			});

			if (hasSelectableDates) {
				monthsToOpen.add(month.index);
			}
		});

		if (monthsToOpen.size === 0) monthsToOpen.add(0);

		return monthsToOpen;
	};

	const init = () => {
		// DEVNOTE: All months with any available dates are open
        // as requested by JetSmart on JET-12202 in 2025.11.18 comment.
		setOpenMonthIndexes(getMonthsToOpen());

		const departureMonthIndex = findMonthIndex(props.currentSearchManager.currentSearch.departureDate);
		const returnMonthIndex = findMonthIndex(props.currentSearchManager.currentSearch.returnDate);

		const scrollToIndex = isSelectingReturn ? returnMonthIndex : departureMonthIndex;
		if (scrollToIndex >= 0) {
			scrollToMonthIndex(scrollToIndex);
		} else {
			scrollToMonthIndex(0);
		}
	};

	const openAsDeparture = () => {
		setIsSelectingReturn(false);
		modal.open();
	};

	const openAsReturn = () => {
		if (
			!props.currentSearchManager.currentSearch.departureDate ||
			props.currentSearchManager.currentSearch.isOneWay
		) {
			return;
		}
		setIsSelectingReturn(true);
		modal.open();
	};

	const toggleMonth = (monthIndex: number) => {
		setOpenMonthIndexes((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(monthIndex)) {
				newSet.delete(monthIndex);
			} else {
				newSet.add(monthIndex);
			}
			return newSet;
		});
	};

	const handleDepartureClick = (date: dayjs.Dayjs, e: MouseEvent) => {
		props.onDepartureChange(date);

		if (props.currentSearchManager.currentSearch.isOneWay) {
			modal.close(e);
		} else {
			setIsSelectingReturn(true);
		}
	};

	const handleReturnClick = (date: dayjs.Dayjs, e: MouseEvent) => {
		props.onReturnChange(date);
		modal.close(e);
	};

	const handleDateClick = (date: dayjs.Dayjs, e: MouseEvent) => {
		if (isSelectingReturn) {
			handleReturnClick(date, e);
		} else {
			handleDepartureClick(date, e);
		}
	};

	const calendarForMonth = useNewFlightDateCalendar({
		availableSchedules: currentAvailableSchedules,
		culture: props.culture,
		disabledDates: currentDisabledDates,
		max: currentMax,
		min: currentMin,
		rangeStart,
		usePrices,
		selectedDate: currentSelectedDate,
		lowestDisplayedPrice: 0,
		onDateClick: handleDateClick,
	});

	const inputsTemplate = () =>
		mobileDateSelectorInputsTemplate({
			departureDate: props.currentSearchManager.currentSearch.departureDate,
			isOneWay: props.currentSearchManager.currentSearch.isOneWay,
			isSelectingReturn,
			returnDate: props.currentSearchManager.currentSearch.returnDate,
			onSelectDeparture: () => setIsSelectingReturn(false),
			onSelectReturn: () => setIsSelectingReturn(true),
		});

	const monthAccordionTemplate = (monthData: { date: dayjs.Dayjs; index: number }) => {
		const isOpen = openMonthIndexes.has(monthData.index);
		const monthName = capitalizeAndRemoveDot(monthData.date.format("MMMM YYYY"));
		const monthCalendar = getMonthCalendar(monthData.date, props.culture);

		// DEVNOTE: Only open accordions render the calendar content
		const contentTemplate = () => (isOpen ? calendarForMonth.getCalendarForMonthTemplate(monthCalendar, true) : "");

		return html`
			<dc-accordion
				class=${classNames("w-full py-2", [`${MONTH_INDEX_CLASS}-${monthData.index}`])}
				.headerClass=${classNames("text-base font-bold text-n-blue pt-3", {
					"border-t border-solid border-[#e0e0e0]": monthData.index !== 0,
					"pb-3": !isOpen,
				})}
				.headerTitle=${monthName}
				.isOpen=${isOpen}
				.onClick=${() => toggleMonth(monthData.index)}
				.contentTemplate=${contentTemplate}
			></dc-accordion>
		`;
	};

	const monthsTemplate = () =>
		!props.timetable.isLoading
			? html`
					<div class=${classNames("flex-1 overflow-y-auto", DATE_SELECTOR_SCROLL_CONTAINER_CLASS)}>
						${monthList.map(monthAccordionTemplate)}
					</div>
			  `
			: "";

	// DEVNOTE: This is memorized to avoid re-rendering the content unnecessarily
	const memoizedContentTemplate = useMemo(
		() => html` <div class="flex h-full flex-col overflow-hidden">${inputsTemplate()} ${monthsTemplate()}</div>`,
		[
			props.timetable.isLoading,
			props.currentSearchManager.currentSearch.departureDate?.format("YYYY-MM-DD"),
			props.currentSearchManager.currentSearch.returnDate?.format("YYYY-MM-DD"),
			props.currentSearchManager.currentSearch.isOneWay,
			isSelectingReturn,
			Array.from(openMonthIndexes).join(","),
			currentAvailableSchedules.length,
			monthList.length,
			currentMin?.format("YYYY-MM-DD"),
			currentMax?.format("YYYY-MM-DD"),
			currentDisabledDates.length,
		]
	);

	const modal = useMobileSearchboxModal({
		title: i18next.t("Elige las fechas"),
		content: memoizedContentTemplate,
	});

	useEffectWithoutCleanup(() => {
		if (!modal.isOpen) return;
		init();
	}, [modal.isOpen, isSelectingReturn]);

	useEffectWithoutCleanup(() => {
		if (!modal.isOpen) {
			setIsSelectingReturn(false);
			setOpenMonthIndexes(new Set([0]));
		}
	}, [modal.isOpen]);

	return {
		...modal,
		openAsDeparture,
		openAsReturn,
	};
};
